import Api from "../../../../../../../../../core/services/api/api";
import {toImportCodeUpper} from "../shared/normalize-spreadsheet-cell-value";

/**
 * @typedef {{ id: *, info: * } | null} VendorInfoCacheEntry
 * `null` = we already asked the server for this vendor code in this run and it was not returned.
 */

/**
 * In-memory vendor-code → id/info cache for one import run (trimmed codes as keys).
 */
export class VendorLookupStore {
    constructor() {
        /** @type {Map<string, VendorInfoCacheEntry>} */
        this._entryByVendorCode = new Map();
    }

    /**
     * Drop all cached entries (e.g. when reusing context across runs).
     */
    clear() {
        this._entryByVendorCode.clear();
    }

    /**
     * Resolves vendor ids for codes not yet in the run cache (one HTTP call per uncached code).
     *
     * @param {string[]} vendorCodesTrimmed
     * @returns {Promise<Map<string, *>>} code → Api response `data` (or null when missing)
     */
    async #fetchVendorDataFromServerOnce(vendorCodesTrimmed) {
        /** @type {Map<string, *>} */
        const dataByCode = new Map();
        for (const code of vendorCodesTrimmed) {
            const vendorResponse = await Api.getVendorInformation(code, false);
            const data = vendorResponse?.data ?? null;
            dataByCode.set(code, data);
        }
        return dataByCode;
    }

    /**
     * @param {string[]} vendorCodesTrimmed — trimmed vendor codes (stable keys)
     * @param {boolean} [disableCache=false] If true, forces a server request for all given codes (bypassing the cache).
     * @returns {Promise<{vendorIds: Record<string, *>, vendorInfo: Record<string, *>}>}
     */
    async resolve(vendorCodesTrimmed, disableCache = false) {
        const unique = [...new Set((vendorCodesTrimmed ?? []).map(toImportCodeUpper).filter(Boolean))];
        if (!unique.length) {
            return {vendorIds: {}, vendorInfo: {}};
        }

        const missing = unique.filter((code) => disableCache || !this._entryByVendorCode.has(code));

        if (missing.length > 0) {
            const fresh = await this.#fetchVendorDataFromServerOnce(missing);

            for (const code of missing) {
                const data = fresh.get(code);
                const id = data?.id ?? null;

                if (id != null && id !== "") {
                    this._entryByVendorCode.set(code, {id, info: data ?? null});
                } else {
                    this._entryByVendorCode.set(code, null);
                }
            }
        }

        const vendorIds = {};
        const vendorInfo = {};
        for (const code of unique) {
            const entry = this._entryByVendorCode.get(code);
            if (entry) {
                vendorIds[code] = entry.id;
                vendorInfo[code] = entry.info;
            }
        }
        return {vendorIds, vendorInfo};
    }
}
